import { Button, Stack } from "@mui/material";
import { useAgentProfile } from "@src/appV2/Agents/api/useAgentProfile";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { format } from "date-fns";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

import { useGetWorkerBookabilityPreference } from "../../api/useGetWorkerBookabilityPreference";
import { usePutWorkerBookabilityPreference } from "../../api/usePutWorkerBookabilityPreference";
import { useUpdateWorkerAvailabilities } from "../../api/useUpdateWorkerAvailability";
import { DEFAULT_AUTO_BOOKING_IS_ENABLED, DEFAULT_WORKER_DISTANCE_CRITERIA } from "../../constants";
import { useWorkerAvailabilityContext } from "../../context";
import { type WorkerAvailabilityCriteriaValues } from "../../types";
import { WorkerAutoBookSwitch } from "../WorkerAutoBookSwitch/WorkerAutoBookSwitch";
import { WorkerAvailabilityCalendar } from "../WorkerAvailabilityCalendar/WorkerAvailabilityCalendar";
import { WorkerDistanceCriteria } from "../WorkerDistanceCriteria/WorkerDistanceCriteria";

export function WorkerAvailabilityCriteria() {
  const history = useHistory();
  const { showErrorToast, showSuccessToast } = useToast();
  const {
    data: agentProfileData,
    isLoading: isLoadingAgentProfile,
    isError: isAgentProfileError,
  } = useAgentProfile();

  const {
    savedAvailableDates,
    isLoading: isLoadingWorkerAvailability,
    alreadyBookedDates,
    calendarOptions,
    timeZone,
    refetchAvailableDates,
  } = useWorkerAvailabilityContext();

  const {
    data: workerBookabilityPreference,
    isLoading: isLoadingWorkerBookabilityPreference,
    isSuccess: isWorkerBookabilityPreferenceSuccess,
    isError: isWorkerBookabilityPreferenceError,
    refetch: refetchWorkerBookabilityPreference,
  } = useGetWorkerBookabilityPreference({ workerId: agentProfileData?.userId });

  const { mutateAsync: mutateBookabilityPreference } = usePutWorkerBookabilityPreference(
    agentProfileData?.userId,
    {
      onSuccess: () => {
        void refetchAvailableDates();
      },
    }
  );

  const { mutateAsync: updateAvailabilities } = useUpdateWorkerAvailabilities({
    onSuccess: () => {
      void refetchWorkerBookabilityPreference();
    },
  });

  const isInitialValuesError = isAgentProfileError || isWorkerBookabilityPreferenceError;
  const isInitialValuesLoading =
    isLoadingWorkerAvailability || isLoadingAgentProfile || isLoadingWorkerBookabilityPreference;

  const workerBookabilityPreferenceAttributes =
    isWorkerBookabilityPreferenceSuccess && "attributes" in workerBookabilityPreference.data
      ? workerBookabilityPreference.data.attributes
      : undefined;

  const formMethods = useForm<WorkerAvailabilityCriteriaValues>({
    values: {
      selectedAvailabilityDates: savedAvailableDates ?? [],
      autoBookingIsEnabled:
        workerBookabilityPreferenceAttributes?.autoBookingIsEnabled ??
        DEFAULT_AUTO_BOOKING_IS_ENABLED,
      distanceInMiles:
        workerBookabilityPreferenceAttributes?.distanceInMiles ?? DEFAULT_WORKER_DISTANCE_CRITERIA,
    },
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = formMethods;

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(async (formValues) => {
          const { selectedAvailabilityDates, distanceInMiles, autoBookingIsEnabled } = formValues;
          try {
            await Promise.all([
              updateAvailabilities({
                tmz: timeZone,
                startDate: format(calendarOptions.minDate, "yyyy-MM-dd"),
                endDate: format(calendarOptions.maxDate, "yyyy-MM-dd"),
                availableDates: selectedAvailabilityDates,
              }),
              mutateBookabilityPreference({
                autoBookingIsEnabled,
                distanceInMiles,
              }),
            ]);
            logEvent(APP_V2_USER_EVENTS.AVAILABILITY_CRITERIA_UPDATED);
            showSuccessToast("Your criteria have been updated!");
            history.push(DeprecatedGlobalAppV1Paths.OPEN_SHIFTS);
          } catch {
            showErrorToast("Something went wrong while saving your criteria. Please retry");
          }
        })}
      >
        <Stack spacing={3}>
          <WorkerAvailabilityCalendar
            calendarOptions={calendarOptions}
            alreadyBookedDates={alreadyBookedDates}
            name="selectedAvailabilityDates"
            isLoading={isInitialValuesLoading}
          />
          <WorkerDistanceCriteria
            isLoading={isInitialValuesLoading}
            name="distanceInMiles"
            isDisabled={isInitialValuesError}
          />
          <WorkerAutoBookSwitch
            isLoading={isInitialValuesLoading}
            name="autoBookingIsEnabled"
            isDisabled={isInitialValuesError}
          />
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={isInitialValuesLoading || isSubmitting || isInitialValuesError}
          >
            Update
          </Button>
        </Stack>
      </form>
    </FormProvider>
  );
}
